import { Link } from "react-router-dom";
import { ArrowRight, Truck, ShieldCheck, Clock, Users, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRANDS_DATA } from "@/data/brandsData";

const HERO_IMAGE = "https://images.unsplash.com/photo-1720036236694-d0a231c52563?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxhdXRvbW90aXZlJTIwc3BhcmUlMjBwYXJ0cyUyMHdhcmVob3VzZSUyMGludGVyaW9yJTIwaW5kdXN0cmlhbHxlbnwwfHx8fDE3NzE3NzE2MDh8MA&ixlib=rb-4.1.0&q=85";


const WHY_CHOOSE = [
{
  icon: ShieldCheck,
  title: "Authorized Distributor",
  desc: "Official authorized distributor of 20+ leading automotive brands, ensuring 100% genuine parts."
},
{
  icon: Clock,
  title: "30+ Years Experience",
  desc: "Serving the automotive aftermarket since 1993 with deep industry knowledge and reliability."
},
{
  icon: Truck,
  title: "Bulk Supply Ready",
  desc: "Large warehouse inventory with rapid fulfilment capability for wholesale and bulk orders."
},
{
  icon: Users,
  title: "Trusted Network",
  desc: "Established relationships with 500+ retailers, wholesalers and workshops across Rajasthan."
}];


export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section
        data-testid="hero-section"
        className="relative min-h-[85vh] flex items-center"
        style={{ marginTop: "calc(36px + 96px)" }}>

        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Automotive spare parts warehouse"
            className="w-full h-full object-cover" />

          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <span className="font-mono text-xs tracking-[0.3em] text-neutral-400 uppercase block mb-6">
              Established 1993 — Jaipur, Rajasthan
            </span>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tight leading-[1.05] mb-6">
              Rajasthan's Trusted Automotive Spare Parts Distributor
            </h1>
            <p className="text-neutral-300 text-base md:text-lg leading-relaxed mb-10 max-w-xl">Authorized distributor of leading brands. Supplying genuine OEM and aftermarket parts to retailers and workshops across the region for over three decades.


            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/brands" data-testid="hero-cta-brands">
                <Button className="bg-[#D32F2F] text-white hover:bg-[#B71C1C] h-12 px-8 text-sm font-heading uppercase font-bold tracking-wide rounded-none cta-btn">
                  Explore Our Brands
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/contact" data-testid="hero-cta-contact">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-black h-12 px-8 text-sm font-heading uppercase font-bold tracking-wide rounded-none cta-btn bg-transparent">
                  Request a Quote
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Logos Grid */}
      <section data-testid="brands-grid-section" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="font-mono text-xs tracking-[0.3em] text-neutral-400 uppercase block mb-3">
                Our Partners
              </span>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl uppercase tracking-tight text-black">
                Brands We Distribute
              </h2>
            </div>
            <Link
              to="/brands"
              data-testid="brands-view-all"
              className="hidden md:flex items-center gap-1 text-sm font-heading uppercase tracking-wider font-semibold text-neutral-500 hover:text-black transition-colors">

              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
            {BRANDS_DATA.slice(0, 15).map((brand) =>
            <Link
              key={brand.name}
              to="/brands"
              data-testid={`brand-card-${brand.name.toLowerCase().replace(/\s/g, "-")}`}
              className="brand-logo-card border border-neutral-200 bg-white p-6 flex flex-col items-center justify-center aspect-square hover:border-black">

                {brand.logo_url ?
              <img
                src={brand.logo_url}
                alt={`${brand.name} logo`}
                className={`max-w-full object-contain mb-3 ${["Rideon", "Gabriel", "AE", "Remsons", "Ars-HTA"].includes(brand.name) ? "max-h-32" : "max-h-16"}`} /> :


              <span className="font-heading font-bold text-lg md:text-xl text-black uppercase tracking-tight text-center mb-3">
                    {brand.name}
                  </span>
              }
                <span className="font-mono text-[9px] tracking-[0.2em] text-neutral-400 uppercase">
                  {brand.category}
                </span>
              </Link>
            )}
          </div>
          <div className="md:hidden mt-8 text-center">
            <Link
              to="/brands"
              className="inline-flex items-center gap-1 text-sm font-heading uppercase tracking-wider font-semibold text-neutral-500">

              View All Brands <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* About Snapshot */}
      <section data-testid="about-snapshot-section" className="py-16 md:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div>
              <span className="font-mono text-xs tracking-[0.3em] text-neutral-400 uppercase block mb-3">
                About R.K. Traders
              </span>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl uppercase tracking-tight text-black mb-6">
                Three Decades of Automotive Excellence
              </h2>
              <p className="text-neutral-600 leading-relaxed mb-6">
                Since 1993, R.K. Traders has been a cornerstone of automotive spare parts distribution 
                in Jaipur. What started as a single-counter operation has grown into one of Rajasthan's 
                most respected distribution houses, serving hundreds of retailers and workshops.
              </p>
              <p className="text-neutral-600 leading-relaxed mb-8">We are authorized distributors for leading national and international brands, with a fully-stocked warehouse and an efficient supply chain network that ensures fast, reliable delivery of genuine automotive components.



              </p>
              <Link to="/about" data-testid="about-read-more">
                <Button className="bg-black text-white hover:bg-neutral-800 h-12 px-8 text-sm font-heading uppercase font-bold tracking-wide rounded-none cta-btn">
                  Learn More About Us
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section data-testid="why-choose-section" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-mono text-xs tracking-[0.3em] text-neutral-400 uppercase block mb-3">
              Why Choose Us
            </span>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl uppercase tracking-tight text-black">
              The R.K. Traders Advantage
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {WHY_CHOOSE.map((item, i) =>
            <div
              key={i}
              data-testid={`why-choose-card-${i}`}
              className="border border-neutral-200 p-8 hover:border-black transition-colors duration-300 group">

                <div className="w-12 h-12 bg-neutral-100 flex items-center justify-center mb-6 group-hover:bg-[#D32F2F] transition-colors duration-300">
                  <item.icon className="w-5 h-5 text-black group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-heading font-bold text-lg uppercase tracking-tight mb-3">
                  {item.title}
                </h3>
                <p className="text-sm text-neutral-500 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-16 md:py-24 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="font-mono text-xs tracking-[0.3em] text-neutral-500 uppercase block mb-4">
            Get in Touch
          </span>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight mb-6">
            Ready to Partner With Us?
          </h2>
          <p className="text-neutral-400 max-w-2xl mx-auto mb-10 text-base md:text-lg">
            Whether you need bulk orders, specific brand enquiries, or want to become a retail 
            partner — we're here to help. Reach out today.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" data-testid="cta-contact-btn">
              <Button className="bg-[#D32F2F] text-white hover:bg-[#B71C1C] h-12 px-10 text-sm font-heading uppercase font-bold tracking-wide rounded-none cta-btn">
                Contact Us Now
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a
              href="https://wa.me/918824893533?text=Hello%20R.K.%20Traders%2C%20I%20would%20like%20to%20enquire%20about%20spare%20parts."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-whatsapp-btn">

              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-black h-12 px-10 text-sm font-heading uppercase font-bold tracking-wide rounded-none cta-btn bg-transparent">
                WhatsApp Enquiry
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>);

}